import type { ContractDownloadBlockReason, ContractVersionAuditPage } from "@closer/shared";
import { CONTRACT_DOWNLOAD_BLOCK_REASON_NAMES } from "@closer/shared";
import { Button, Card, EmptyState, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * VERSİYON AUDIT GEÇMİŞİ (CLOS-143 `[102]`).
 *
 * ### ⚠ Yetkisi olmayan için bu bölüm HİÇ ÇİZİLMİYOR
 *
 * Kart iki yetkiyi ayırıyor: geçmiş `contract.history`, içerik `contract.view`. Bölümü
 * herkese çizip içine hata koysaydık, sözleşmeyi okuyabilen ama denetim yetkisi olmayan
 * kullanıcıya "burada göremediğin bir geçmiş var" demiş olurduk. Karar yine SUNUCUDA —
 * `can()` yalnız gereksiz bir isteği ve boş bir kutuyu önlüyor.
 *
 * ### ⚠ Sayfalama İMLEÇLE
 *
 * Audit araya kayıt alır: sayfa numarasıyla ilerleyen bir liste, ikinci sayfada aynı
 * kaydı ikinci kez gösterir ya da bir kaydı hiç göstermez. İmleç son görülen ZAMANI
 * taşıyor.
 */

function tarih(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR");
}

export function ContractVersionAudit({
  contractId,
  versionId,
}: {
  contractId: string;
  versionId: string;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const [imlecler, setImlecler] = useState<string[]>([]);
  const imlec = imlecler.at(-1) ?? null;

  const yetkili = can("contract.history");

  const sorgu = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(
      tenantId,
      "contract",
      contractId,
      "versiyon",
      versionId,
      "gecmis",
      imlec ?? "ilk",
    ),
    queryFn: async () =>
      (
        await api<ContractVersionAuditPage>(
          `/api/contracts/${contractId}/versions/${versionId}/audit?limit=20${
            imlec ? `&before=${encodeURIComponent(imlec)}` : ""
          }`,
          { tenantId },
        )
      ).data,
  });

  if (!yetkili) return null;

  if (sorgu.isPending) {
    return (
      <Card title="Versiyon geçmişi">
        <div role="status" aria-label="Geçmiş yükleniyor">
          <Skeleton lines={3} />
        </div>
      </Card>
    );
  }

  if (sorgu.error || !sorgu.data) {
    return (
      <Card title="Versiyon geçmişi">
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            overrides: {
              /**
               * ⚠ Erişim kaybında da AYNI cevap: sunucu 404 veriyor çünkü "var ama sana
               * kapalı" demek kaydın varlığını doğrulamak olurdu (`[079]`).
               */
              forbiddenOrMissing: {
                title: "Geçmiş görüntülenemiyor",
                description: "Bu versiyonun geçmişi yok ya da erişiminiz bulunmuyor.",
              },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
      </Card>
    );
  }

  const sayfa = sorgu.data;

  return (
    <Card title="Versiyon geçmişi">
      {sayfa.items.length === 0 ? (
        <EmptyState
          title="Kayıt yok"
          description="Bu versiyon için görüntüleyebileceğiniz bir olay bulunmuyor."
        />
      ) : (
        <ol className="flex flex-col gap-3">
          {sayfa.items.map((olay) => (
            <li
              key={olay.id}
              className="flex flex-col gap-0.5 border-l-2 border-border-subtle pl-3"
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-medium text-fg-strong">{olay.eventName}</span>
                <span className="text-xs text-fg-faint">{tarih(olay.createdAt)}</span>
              </div>
              {/* Aktör ADI; ham kullanıcı kimliği sunucudan hiç gelmiyor. */}
              <span className="text-xs text-fg-soft">
                {olay.actorName}
                {olay.actorRole ? ` · ${olay.actorRole}` : ""}
              </span>
              {/* Sebep KATALOGDAN okunuyor; ekran ham enum adını göstermiyor (refleks 4). */}
              {typeof olay.detail.reason === "string" && (
                <span className="text-xs text-fg-soft">
                  Sebep:{" "}
                  {CONTRACT_DOWNLOAD_BLOCK_REASON_NAMES[
                    olay.detail.reason as ContractDownloadBlockReason
                  ] ?? olay.detail.reason}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}

      <div className="mt-3 flex gap-2">
        {imlecler.length > 0 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setImlecler((ö) => ö.slice(0, -1))}
          >
            Önceki
          </Button>
        )}
        {sayfa.nextCursor && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setImlecler((ö) => [...ö, sayfa.nextCursor!])}
          >
            Daha eski
          </Button>
        )}
      </div>
    </Card>
  );
}
